import React from 'react';
import { handlePasswordResetAction } from 'helpers/passwordResetHelper';
import { masterDataApi } from 'services/masterDataApi';
import { useActionData, useLoaderData } from 'react-router-dom';
import { useNavigationState } from 'hooks/useNavigationState';
import { ReactComponent as WFHLogo } from 'assets/WorkforceHubDarkLogo.svg';
import { usePasswordResetInputHandler } from 'hooks/usePasswordResetInputHandler';
import PasswordResetProvider, {
  FORM_IDS,
} from 'providers/PasswordResetProvider';
import { usePasswordValidationCallback } from 'hooks/usePasswordValidationCallback';
import { setPublicPageTitle } from 'helpers/documentHelper';
import {
  Background,
  CardFooter,
  CardForm,
  ErrorMessage,
  FormContainer,
  FormDescription,
  FormHeader,
  LogoContainer,
  PageGrid,
  PageCenterColumn,
  PageHeader,
} from './PublicStyles';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import NotVerified from './NotVerified';
import PasswordTextfield from 'components/uncontrolled/PasswordTextfield';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';

// Create an anchor to pass to the getURL to generate
// the data URL for embedding
export async function action(store, { params, request }) {
  return handlePasswordResetAction(
    store,
    { params, request },
    masterDataApi.endpoints.acceptEmployeeInvite,
    'We encountered an issue resetting your password. Please try again later or reach out to support for further assistance.'
  );
}

export async function loader(store, { params }) {
  const { employeeId, guid } = params ?? {};

  const verifyInviteResponse = store.dispatch(
    masterDataApi.endpoints.verifyEmployeeInvite.initiate({ employeeId, guid })
  );

  // We want to swallow this error and just return false
  // So wrapping in a try/catch to handle instead of letting it bubble up
  let verified = false;
  try {
    await verifyInviteResponse.unwrap();
    verified = true;
  } catch (error) {
    verified = false;
  }

  setPublicPageTitle('Invite');

  return { verified };
}

export default function AcceptInvite() {
  const { error } = useActionData() ?? {};
  const { verified } = useLoaderData();

  return (
    <PasswordResetProvider>
      <Background>
        <PageGrid>
          <PageCenterColumn>
            <PageHeader>WorkforceHub Invite</PageHeader>
            <CardForm method="post" replace>
              {!verified && <NotVerified />}
              {verified && <AcceptInviteForm error={error} />}
            </CardForm>
            <LogoContainer>
              <WFHLogo />
            </LogoContainer>
          </PageCenterColumn>
        </PageGrid>
      </Background>
    </PasswordResetProvider>
  );
}

function AcceptInviteForm({ error }) {
  const isLoading = useNavigationState();
  const validationCallback = usePasswordValidationCallback();
  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid,
    validationRef,
  } = usePasswordResetInputHandler({
    ids: [FORM_IDS.PASSWORD, FORM_IDS.CONFIRM_PASSWORD],
    validationCallback,
  });

  return (
    <>
      <FormContainer>
        <FormHeader>You have been invited to a WorkforceHub site.</FormHeader>
        {error && <ErrorMessage error={error} />}
        <FormDescription>
          Please set your password below and we will redirect you to the site
          once complete:
        </FormDescription>
        <Stack gap={3} sx={{ my: 1 }}>
          <PasswordTextfield
            autoComplete="password"
            error={getError(FORM_IDS.PASSWORD)}
            id={FORM_IDS.PASSWORD}
            label="Password"
            maxLength={50}
            onBlur={handleBlur(FORM_IDS.PASSWORD)}
            onChange={handleChange(FORM_IDS.PASSWORD)}
            defaultValue={''}
            placeholder="Enter your password"
            required
            validationRef={validationRef}
          />
          {/* Confirm Password */}
          <PasswordTextfield
            autoComplete="password"
            error={getError(FORM_IDS.CONFIRM_PASSWORD)}
            id={FORM_IDS.CONFIRM_PASSWORD}
            label="Confirm Password"
            maxLength={50}
            onBlur={handleBlur(FORM_IDS.CONFIRM_PASSWORD)}
            onChange={handleChange(FORM_IDS.CONFIRM_PASSWORD)}
            defaultValue={''}
            placeholder="Enter your password again"
            required
            validationRef={validationRef}
          />
        </Stack>
      </FormContainer>
      <Divider />
      <CardFooter>
        <Button
          name="accept"
          type="submit"
          value="acceptInvite"
          endArrow
          disabled={disabled || !isValid}
          loading={isLoading}
        >
          Accept Invite
        </Button>
      </CardFooter>
    </>
  );
}

AcceptInviteForm.propTypes = {
  error: PropTypes.object,
};
